/**
 * URL Sanitization Utility
 * 
 * Provides functions to sanitize URLs and prevent XSS attacks through image sources
 * and other URL-based attributes.
 * 
 * @author Security Team
 * @version 1.0.0
 */

/**
 * Sanitizes a URL to prevent XSS attacks
 * 
 * @param {string} url - The URL to sanitize
 * @param {Object} options - Configuration options
 * @param {boolean} options.allowDataUrls - Whether to allow data: URLs (default: false)
 * @param {boolean} options.allowBlob - Whether to allow blob: URLs (default: false)
 * @param {Array<string>} options.allowedDomains - List of allowed domains (optional)
 * @returns {string} - Sanitized URL or empty string if invalid
 */
export const sanitizeUrl = (url, options = {}) => {
    const {
        allowDataUrls = false,
        allowBlob = false,
        allowedDomains = null
    } = options;

    // Basic validation
    if (!url || typeof url !== 'string') {
        return '';
    }

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
        return '';
    }

    // Convert to lowercase for protocol checking
    const lowerUrl = trimmedUrl.toLowerCase();

    // Block protocol-relative URLs (e.g. //example.com) which inherit the page protocol
    // and can be abused or bypass protocol checks. Treat them as invalid.
    if (trimmedUrl.startsWith('//')) {
        return '';
    }

    // Define dangerous protocols
    const dangerousProtocols = [
        'javascript',
        'vbscript',
        'file',
        'about',
        'chrome',
        'chrome-extension',
        'moz-extension'
    ];

    // Add data to dangerous protocols if not explicitly allowed
    if (!allowDataUrls) {
        dangerousProtocols.push('data');
    }

    // Add blob to dangerous protocols if not explicitly allowed
    if (!allowBlob) {
        dangerousProtocols.push('blob');
    }

    // Check for dangerous protocols
    for (const protocol of dangerousProtocols) {
        if (lowerUrl.startsWith(protocol + ':')) {
            return '';
        }
    }

    // Additional validation for data URLs
    if (lowerUrl.startsWith('data:')) {
        // For data URLs, ensure they are image types to prevent XSS
        const dataUrlMatch = trimmedUrl.match(/^data:([^;]+)/);
        if (!dataUrlMatch || !dataUrlMatch[1].startsWith('image/')) {
            return '';
        }
    }

    // Allow safe protocols
    const safeProtocols = ['http://', 'https://'];
    const isAbsoluteUrl = safeProtocols.some(protocol => lowerUrl.startsWith(protocol));

    if (isAbsoluteUrl) {
        // For absolute URLs, check allowed domains if specified
        if (allowedDomains && allowedDomains.length > 0) {
            try {
                const urlObj = new URL(trimmedUrl);
                const domain = urlObj.hostname.toLowerCase();
                
                const isDomainAllowed = allowedDomains.some(allowedDomain => 
                    domain === allowedDomain.toLowerCase() || 
                    domain.endsWith('.' + allowedDomain.toLowerCase())
                );

                if (!isDomainAllowed) {
                    return '';
                }
            } catch (e) {
                return '';
            }
        }
        return trimmedUrl;
    }

    // Allow relative URLs
    if (lowerUrl.startsWith('/') || lowerUrl.startsWith('./') || lowerUrl.startsWith('../')) {
        return trimmedUrl;
    }

    // If it doesn't contain a protocol, treat as relative URL
    if (!lowerUrl.includes('://')) {
        return trimmedUrl;
    }

    // Block anything else
    return '';
};

/**
 * Sanitizes an image URL specifically for use in img src attributes
 * 
 * @param {string} url - The image URL to sanitize
 * @param {Object} options - Configuration options
 * @returns {string} - Sanitized URL or empty string if invalid
 */
export const sanitizeImageUrl = (url, options = {}) => {
    // Allow data URLs for images by default (base64 encoded images are common)
    const imageOptions = {
        allowDataUrls: true,
        allowBlob: true,
        ...options
    };

    return sanitizeUrl(url, imageOptions);
};

/**
 * Sanitizes text content to prevent XSS attacks
 * 
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text with HTML entities escaped
 */
export const sanitizeText = (text) => {
    if (!text || typeof text !== 'string') {
        return '';
    }
    
    // Remove any HTML tags first
    let sanitized = text.replace(/<[^>]*>/g, '');
    
    // Escape dangerous characters
    sanitized = sanitized
        .replace(/&/g, '&amp;')   // Must be first to avoid double-escaping
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;')
        .replace(/\\/g, '&#x5C;')
        .replace(/`/g, '&#96;')
        .replace(/=/g, '&#61;');
    
    // Remove control characters and normalize whitespace
    sanitized = sanitized
        .replace(/[\0-\x1F\x7F-\x9F]/g, '') // eslint-disable-line no-control-regex
        .replace(/\s+/g, ' ')
        .trim();
    
    return sanitized;
};

/**
 * Sanitizes a URL for use in href attributes
 * 
 * @param {string} url - The URL to sanitize
 * @param {Object} options - Configuration options
 * @returns {string} - Sanitized URL or '#' if invalid
 */
export const sanitizeLinkUrl = (url, options = {}) => {
    const sanitized = sanitizeUrl(url, options);
    return sanitized || '#';
};

/**
 * Creates a safe image source with fallback
 * 
 * @param {string} primaryUrl - Primary image URL
 * @param {string} fallbackUrl - Fallback image URL
 * @param {Object} options - Configuration options
 * @returns {string} - Sanitized URL with fallback
 */
export const createSafeImageSrc = (primaryUrl, fallbackUrl = '', options = {}) => {
    const sanitizedPrimary = sanitizeImageUrl(primaryUrl, options);
    if (sanitizedPrimary) {
        return sanitizedPrimary;
    }

    const sanitizedFallback = sanitizeImageUrl(fallbackUrl, options);
    return sanitizedFallback;
};

/**
 * Validates and creates a complete image URL from base path and relative path
 * 
 * @param {string} basePath - Base path for images
 * @param {string} relativePath - Relative path to image
 * @param {Object} options - Configuration options
 * @returns {string} - Complete sanitized URL
 */
export const createSafeImagePath = (basePath, relativePath, options = {}) => {
    if (!basePath || !relativePath) {
        return '';
    }

    // Ensure base path ends with /
    const cleanBasePath = basePath.endsWith('/') ? basePath : basePath + '/';
    
    // Remove leading / from relative path
    const cleanRelativePath = relativePath.startsWith('/') ? relativePath.substring(1) : relativePath;
    
    const fullUrl = cleanBasePath + cleanRelativePath;
    return sanitizeImageUrl(fullUrl, options);
};

// Default export for backward compatibility
export default {
    sanitizeUrl,
    sanitizeImageUrl,
    sanitizeLinkUrl,
    sanitizeText,
    createSafeImageSrc,
    createSafeImagePath
};
